// Ekran 01'in drawer içerikleri — ref_files/btys-erp-01-stok-karti.html
// openDrawer('kopya'|'literatur'|'import') ve quickNew('cari').
//
// Hepsi gerçek çalışır: "Kopyala ve kaydet" gerçekten kart oluşturur.
// Her bileşen kendi .dr-b ve .dr-f'ini basar (referans da bu ikisini ayrı doldurur).

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { cariler, stokKartlari, type StokKartiOzet } from './api'

// ---------------------------------------------------------------- kopyala

export function KopyaDrawer({ kart }: { kart: StokKartiOzet }) {
  const { kapat } = useDrawer()
  const [kod, setKod] = useState('')
  const [ad, setAd] = useState(`${kart.ad} (Kopya)`)
  const [bekliyor, setBekliyor] = useState(false)
  const adRef = useRef<HTMLInputElement>(null)
  const kuyruk = useQueryClient()

  useEffect(() => {
    void stokKartlari.sonrakiKod(kart.tur).then((y) => setKod(y.kod))
  }, [kart.tur])

  // Referans: açılışta ad alanına odaklan ve metni seç.
  useEffect(() => {
    const z = setTimeout(() => {
      adRef.current?.focus()
      adRef.current?.select()
    }, 60)
    return () => clearTimeout(z)
  }, [])

  async function kaydet() {
    if (!kod || bekliyor) return
    setBekliyor(true)
    try {
      await stokKartlari.kopyala(kart.id, kod, ad)
      await Promise.all([
        kuyruk.invalidateQueries({ queryKey: ['kartlar'] }),
        kuyruk.invalidateQueries({ queryKey: ['kart-sayaclari'] }),
        kuyruk.invalidateQueries({ queryKey: ['kart-ozeti'] }),
      ])
      kapat()
      toast(`${kod} oluşturuldu — ${kart.kod} kopyası`)
    } catch (e) {
      toast(e instanceof ApiHatasi ? e.message : 'Kopyalanamadı.')
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Tüm parametreler, tesis değerleri ve ayarlar aynen kopyalanır. Yeni kartın
            kaynağı <b>Özel</b>, stok bakiyesi <b>0</b> olarak başlar.
          </div>
        </div>
        <div className="fgrid">
          <div className="fld">
            <label>Yeni kod</label>
            <input className="in mono" value={kod} readOnly />
          </div>
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Yeni ad</label>
            <input ref={adRef} className="in" value={ad} onChange={(e) => setAd(e.target.value)} />
          </div>
        </div>
      </DrawerGovde>
      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!kod || bekliyor}
          onClick={() => void kaydet()}
        >
          {bekliyor ? 'Kaydediliyor…' : 'Kopyala ve kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}

// ------------------------------------------------------------- literatür

/**
 * K5 literatür kütüphanesi. CLAUDE.md tabloyu ve Excel import ucunu Faz 2'ye
 * bırakıyor ("şema hazır"); referans ekran bu 8 kaydı gömülü tutuyor. Tablo
 * geldiğinde kaynak değişecek, davranış değişmeyecek.
 */
const KUTUPHANE: [string, string, number][] = [
  ['Küçükbaş Gübresi (Koyun-Keçi)', 'Hayvansal', 400],
  ['Hindi Gübresi', 'Hayvansal', 480],
  ['Çim/Ot Silajı', 'Bitkisel', 560],
  ['Şeker Pancarı (Kök)', 'Bitkisel', 680],
  ['Bira Posası', 'Gıda Sanayi', 550],
  ['Melas', 'Gıda Sanayi', 500],
  ['Rumen/İşkembe İçeriği', 'Mezbaha', 450],
  ['Ham Gliserin (Biyodizel)', 'Endüstriyel', 880],
]

export function LiteraturDrawer() {
  const { kapat } = useDrawer()
  // Referansta ilk iki kayıt seçili açılır.
  const [secili, setSecili] = useState<Set<number>>(new Set([0, 1]))
  const [arama, setArama] = useState('')

  const gorunen = KUTUPHANE.map((l, i) => ({ l, i })).filter(
    ({ l }) =>
      !arama ||
      l[0].toLocaleLowerCase('tr').includes(arama.toLocaleLowerCase('tr')) ||
      l[1].toLocaleLowerCase('tr').includes(arama.toLocaleLowerCase('tr')),
  )

  function degistir(i: number) {
    setSecili((s) => {
      const y = new Set(s)
      if (y.has(i)) y.delete(i)
      else y.add(i)
      return y
    })
  }

  return (
    <>
      <DrawerGovde>
        <div className="f-search" style={{ width: '100%', marginBottom: 11 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            placeholder="Kütüphanede ara…"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
          />
        </div>
        {gorunen.map(({ l, i }) => (
          <label className="lib-i" key={l[0]}>
            <input type="checkbox" checked={secili.has(i)} onChange={() => degistir(i)} />
            <div>
              <div className="lib-n">{l[0]}</div>
              <div className="lib-m">{l[1]} · KTBL/VDI 4630</div>
            </div>
            <span className="lib-v">{l[2]} Nm³/t oKM</span>
          </label>
        ))}
      </DrawerGovde>
      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={secili.size === 0}
          onClick={() => {
            const n = secili.size
            kapat()
            // Kart üretimi literatür tablosuna bağlı (K5, Faz 2) — "eklendi"
            // demek yerine durumu dürüstçe söylüyoruz.
            toast(`${n} kayıt seçildi — literatür tablosu (K5) Faz 2'de bağlanacak`)
          }}
        >
          Seçilenleri ekle ({secili.size})
        </button>
      </DrawerAltlik>
    </>
  )
}

// ---------------------------------------------------------------- import

export function ImportDrawer() {
  const { kapat } = useDrawer()
  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Kolon başlıkları şablonla birebir aynı olmalıdır. Kül ve C/N içe aktarımda
            hesaplanır.
          </div>
        </div>
        <div
          style={{
            border: '1.5px dashed var(--line)',
            borderRadius: 8,
            padding: '30px 14px',
            textAlign: 'center',
            color: 'var(--muted)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>
            Dosyayı buraya bırakın
          </div>
          <div style={{ fontSize: 11 }}>
            veya{' '}
            <span style={{ color: 'var(--navy-500)', fontWeight: 600 }}>bilgisayardan seçin</span> ·
            .xlsx, en çok 5 MB
          </div>
        </div>
      </DrawerGovde>
      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button className="btn btn-pri" type="button" disabled style={{ opacity: 0.5 }}>
          İçe aktar
        </button>
      </DrawerAltlik>
    </>
  )
}

// --------------------------------------------------- iç içe hızlı kayıt

/**
 * quickNew('cari') — 2. seviye drawer. Referansın vaadi "mevcut işleminiz açık
 * kalır": 1. seviye kapanmaz, <c>.pushed</c> alır (bkz. DrawerYuvasi).
 */
export function HizliCariDrawer() {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [unvan, setUnvan] = useState('')

  /**
   * Referansta bu düğme yalnız "eklendi ve seçildi" toast'ı basıyordu; ne cari
   * oluşuyor ne de seçim yapılıyordu. Artık gerçekten kaydediyor. Kodu sunucu
   * üretmiyor, bu yüzden unvandan türetilen geçici bir kod veriliyor ve
   * kullanıcı Cariler ekranından düzeltebiliyor.
   */
  const olustur = useMutation({
    mutationFn: () =>
      cariler.kaydet(null, {
        kod: `C-${Date.now().toString(36).toUpperCase().slice(-6)}`,
        unvan: unvan.trim(),
        rolTedarikci: true,
        durum: 'aktif',
      }),
    onSuccess: async (c) => {
      await kuyruk.invalidateQueries({ queryKey: ['cariler'] })
      kapat()
      toast(`${c.kod} · ${c.unvan} eklendi — tedarikçi rolüyle`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Cari eklenemedi.'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Cari <b>Cariler</b> listesine eklenir. Açık formdaki tedarikçi alanına
            otomatik seçilmesi henüz bağlanmadı — kaydettikten sonra listeden seçin.
          </div>
        </div>
        <div className="fgrid">
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Unvan</label>
            <input
              className="in"
              placeholder="Örn. Anadolu Kimya A.Ş."
              value={unvan}
              onChange={(e) => setUnvan(e.target.value)}
            />
          </div>
          <div className="fld">
            <label>Tip</label>
            <select className="in">
              <option>Tedarikçi</option>
              <option>Atık üreticisi</option>
              <option>Nakliyeci</option>
            </select>
          </div>
          <div className="fld">
            <label>VKN</label>
            <input className="in mono" />
          </div>
        </div>
      </DrawerGovde>
      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!unvan.trim() || olustur.isPending}
          onClick={() => olustur.mutate()}
        >
          Cariyi kaydet
        </button>
      </DrawerAltlik>
    </>
  )
}

/**
 * quickNew('kategori') — 2. seviye drawer (ref:694-715).
 *
 * Kategori kütüphanesi ayrı bir tablo DEĞİL: §5.1'de `stok_karti.grup` serbest
 * metin kolonu, kart PUT'u onu zaten yazıyor. Bu yüzden drawer'ın kendi ucu yok;
 * gerçek yan etki çağırana bırakılıyor (`onSec`): kategori açık formun listesine
 * girer ve seçili gelir — kalıcı olması kartın kaydedilmesine bağlı.
 */
export function HizliKategoriDrawer({
  mevcutlar,
  onSec,
}: {
  /** Formdaki seçenek listesi — aynı adı ikinci kez eklemeyi engeller. */
  mevcutlar: string[]
  onSec: (ad: string) => void
}) {
  const { kapat } = useDrawer()
  const [ad, setAd] = useState('')
  const adRef = useRef<HTMLInputElement>(null)

  // Referans quickNew de açılışta ad alanına odaklanıyor.
  useEffect(() => {
    const z = setTimeout(() => adRef.current?.focus(), 60)
    return () => clearTimeout(z)
  }, [])

  const temiz = ad.trim()
  const tekrar = mevcutlar.some(
    (g) => g.toLocaleLowerCase('tr') === temiz.toLocaleLowerCase('tr'),
  )

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Kaydedince kategori, açık formdaki kategori alanına <b>otomatik seçili</b>{' '}
            gelir. Kalıcı olması için kartı kaydedin.
          </div>
        </div>
        <div className="fgrid">
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Kategori adı</label>
            <input
              ref={adRef}
              className="in"
              placeholder="Örn. Deniz Ürünleri Atıkları"
              value={ad}
              onChange={(e) => setAd(e.target.value)}
            />
          </div>
        </div>
      </DrawerGovde>
      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!temiz || tekrar}
          title={
            !temiz
              ? 'Kategori adı boş'
              : tekrar
                ? 'Bu kategori listede zaten var'
                : undefined
          }
          onClick={() => {
            onSec(temiz)
            kapat()
          }}
        >
          Kaydet ve seç
        </button>
      </DrawerAltlik>
    </>
  )
}
